// src/components/sections/EtEternitySolutions.jsx
import { motion } from "framer-motion";
import {
  Lightbulb,
  Cog,
  LayoutGrid,
  Users,
  BarChart2,
  Rocket,
  Sparkles,
} from "lucide-react";

import {
  etApproach1,
  etApproach2,
  etApproach3,
  etApproach4,
  etApproach5,
  etApproach6,
  etApproach7,
} from "@/assets/img/imageAssets.js"

const EtEternitySolutions = () => {
  // First Created - 07 Oct 2025
  // Author - Atul Pandey
  // Purpose - Immersive, alternating narrative flow showing Eternity’s inductive approach
  // Updated - 25 Nov 2025 - Simplified Language

  // 1️⃣ Section Data
  // 25 Nov
  const sections = [
    {
      title: "What’s our Approach?",
      icon: Lightbulb,
      text: (
        // 25 Nov 2025
        <>
            We don’t tell people what to do.  
            We help them <strong className="text-orange-500">discover it for themselves</strong>.

            <br /><br />

            We help each leader get clear about what makes them strong,  
            what gives them energy, and what truly matters.

            <br /><br />

            Once that clarity arrives, we then help them build better habits,  
            leading to <strong className="text-orange-400">clearer thinking</strong> and  
            <strong className="text-orange-400"> better team alignment</strong>.

            <br /><br />
            That’s the whole idea.
          </>

      ),
      // text: (
      //   <>
      //     Inductive, not instructive — <strong>we <span className="text-orange-500 animate-pulse">magnetize</span>, not tell.</strong>  
      //     We help leaders uncover their <strong>untapped strengths</strong> and align personal purpose with organizational purpose.  
      //     The shift is <strong> <span className="text-orange-400">purpose-driven</span></strong> — translating deeper awareness into practical habit change that enhances communication, collaboration, and clarity.
      //   </>
      // ),
      image: etApproach1,
    },
    {
      title: "How Does It Work?",
      icon: Cog,
      text: (
        // 25 Nov 2025
            <>
              The journey begins with the <strong className="text-orange-500">top executives (the CxOs)</strong>.

              <br /><br />

              Through focused, private 1:1 conversations and group sessions,  
              we help each leader to create a clear and measurable  
              <strong className="text-orange-600"> Transformation Charter</strong> —  
              their own plan for growth.

              <br /><br />

              <strong className="text-orange-600">Our AI companion Trinity</strong> becomes the daily vehicle  
              that helps them reflect, stay consistent, and build new habits, everyday, one day at a time.
            </>

        // <>
        //   The journey <strong>begins with the <span className="text-orange-500 animate-pulse">CxOs</span></strong>.  
        //   Through consultative 1:1s and collective sessions, each leader crafts a measurable <strong> <span className="text-orange-400">Transformation Charter</span></strong>.  
        //   <strong> <span className="text-orange-500 animate-pulse">Trinity</span></strong> — Eternity’s AI-enabled performance companion — becomes the vehicle of engagement, enabling structured reflection and continuous reinforcement of new behaviors.
        // </>
      ),
      image: etApproach2,
    },
    {
      // title: "How Is It Structured?",
      title: "What is the engagement like?",
      icon: LayoutGrid,
      //25 Nov 2025
      text: (
        <>
          The engagement spans one year. A  
          <strong className="text-orange-600"> 3-month induction </strong>  
          followed by a  
          <strong className="text-orange-600"> 9-month implementation </strong>  
          phase where real habits take root.

          <br /><br />

          We start with a focused group of  
          <strong className="text-orange-600"> 5–15 leaders </strong>.  
          As they grow, we help them naturally influence their teams and the wider organization.

          <br /><br />

          <strong className="text-orange-600">Trinity</strong> supports the entire process.  
          Helping each leader stay consistent and reinforcing the new behaviors every day.
        </>

      ),
      // text: (
      //   <>
      //     A <strong>3-month induction</strong> followed by a <strong>9-month implementation</strong> creates measurable performance shifts.  
      //     Cohorts of <span className="text-orange-400">5–15</span> leaders — <span className="text-orange-500 animate-pulse">“Magnets”</span> — activate wider teams.  
      //     Larger rollouts follow across key accounts, with Trinity sustaining daily behavioral reinforcement.
      //   </>
      // ),
      image: etApproach3,
    },
    {
      title: "Sounds Like Coaching?",
      icon: Users,
      // 25 Nov 2025
      text: (
        <>
          This is not traditional coaching.

          <br /><br />

          Leaders do not receive instructions.  
          They work with us to understand what needs to change  
          and design the change for themselves.

          <br /><br />

          The growth spreads naturally through small, daily actions.  
          <strong className="text-orange-600"> Trinity</strong> supports this by guiding reflections  
          and helping leaders stay consistent every day.
        </>

      ),
      // text: (
      //   <>
      //     Not traditional coaching.  
      //     This is an <strong>inductive, investigative, <span className="text-orange-400">performance-anchored</span> engagement</strong> where leaders co-design and implement their own change charters.  
      //     Transformation multiplies organically through daily engagements. Amplified by <strong>Trinity’s AI-guided reflections</strong> and intrinsic motivation.
      //   </>
      // ),
      image: etApproach4,
    },
    {
      // title: "What’s the Primary Outcome Focus?",
      title: "What are we helping change?",
      icon: BarChart2,
      // 25 Nov 2025
      text: (
        <>
          <strong className="text-orange-500">
            Client focus comes first.
          </strong>

          <br /><br />

          Understanding the client.  
          Helping them.  
          Delivering well.  
          Creating real value.

          <br /><br />

          When leaders grow, the organization becomes stronger  
          at earning <strong className="text-orange-500">client trust and value</strong>.

          <br /><br />

          Every measure of success connects to how well  
          client value is created and sustained.
        </>

      ),
      // text: (
      //   <>
      //     <strong><span className="text-orange-500">Unwavering <span className="text-orange-500 animate-pulse">client focus.</span></span></strong> Understanding, delivering, advising, creating.  
      //     Every link of performance strengthens the organization’s ability to generate <strong>client trust and value</strong>.  
      //     All metrics of success connect directly to how effectively client value is created and sustained.
      //   </>
      // ),
      image: etApproach5,
    },
    {
      title: "Is It for Sales or Delivery?",
      icon: Rocket,
      // 25 Nov 2025
      text: (
        <>
          <strong>Both.</strong> However, <strong className="text-orange-500">Delivery</strong> is what sets a company apart.

          <br /><br />

          We believe everyone in a tech organization can contribute to growth.  
           It is not limited to the sales team.  
          Anyone who works with a client can influence, advise,  
          and create meaningful impact.

          <br /><br />

          When <strong className="text-orange-500">delivery excellence</strong> becomes a habit,  
          <strong className="text-orange-500"> growth</strong> follows naturally.
        </>

      ),
      // text: (
      //   <>
      //     <strong><span className="text-orange-500">Delivery</span> is the differentiator.</strong>  
      //     <span className="text-orange-500 animate-pulse"> Eternity’s belief:</span>  everyone in a tech-led organization  is a potential seller. Capable of influencing, advising, and creating meaningful client impact.  
      //     We build organizations where <strong>delivery excellence equals growth momentum</strong>.
      //   </>
      // ),
      image: etApproach6,
    },
    {
      title: "Why start at the top?",
      icon: Sparkles,
      // 25 Nov 2025
      text: (
        <>
          The engagement begins with the
          <strong className="text-orange-500"> senior leaders</strong>.  
          They set the energy, the direction, and the inspiration for everyone else.

          <br /><br />

          When they participate with intent,  
          the entire organization feels the shift.  
          Their influence creates a strong
          <strong className="text-orange-500"> field of momentum </strong>  
          that others naturally follow.

          <br /><br />

          They help build a culture that moves on its own and performs with consistency.  
          <strong className="text-orange-600"> Trinity</strong> keeps this energy alive every day.
        </>

      ),
      // text: (
      //   <>
      //     CxOs are the <strong><span className="text-orange-500">prime movers.</span></strong> The most powerful source of energy and inspiration.  
      //     Their active participation defines the <strong><span className="text-orange-500 animate-pulse">magnetic field</span></strong> for the rest of the organization.  
      //     They are catalysts who build a <strong>self-driving, high-performance culture</strong>. Powered and perpetuated through Trinity.
      //   </>
      // ),
      image: etApproach7,
    },
  ];

  // Prior to 25 Nov 2025
  // const sections = [
  //   {
  //     title: "What’s the Approach?",
  //     icon: Lightbulb,
  //     text: (
  //       <>
  //         Inductive, not instructive — <strong>we <span className="text-orange-500 animate-pulse">magnetize</span>, not tell.</strong>  
  //         We help leaders uncover their <strong>untapped strengths</strong> and align personal purpose with organizational purpose.  
  //         The shift is <strong> <span className="text-orange-400">purpose-driven</span></strong> — translating deeper awareness into practical habit change that enhances communication, collaboration, and clarity.
  //       </>
  //     ),
  //     image: etApproach1,
  //   },
  //   {
  //     title: "How Does It Work?",
  //     icon: Cog,
  //     text: (
  //       <>
  //         The journey <strong>begins with the <span className="text-orange-500 animate-pulse">CxOs</span></strong>.  
  //         Through consultative 1:1s and collective sessions, each leader crafts a measurable <strong> <span className="text-orange-400">Transformation Charter</span></strong>.  
  //         <strong> <span className="text-orange-500 animate-pulse">Trinity</span></strong> — Eternity’s AI-enabled performance companion — becomes the vehicle of engagement, enabling structured reflection and continuous reinforcement of new behaviors.
  //       </>
  //     ),
  //     image: etApproach2,
  //   },
  //   {
  //     title: "How Is It Structured?",
  //     icon: LayoutGrid,
  //     text: (
  //       <>
  //         A <strong>3-month induction</strong> followed by a <strong>9-month implementation</strong> creates measurable performance shifts.  
  //         Cohorts of <span className="text-orange-400">5–15</span> leaders — <span className="text-orange-500 animate-pulse">“Magnets”</span> — activate wider teams.  
  //         Larger rollouts follow across key accounts, with Trinity sustaining daily behavioral reinforcement.
  //       </>
  //     ),
  //     image: etApproach3,
  //   },
  //   {
  //     title: "Sounds Like Coaching?",
  //     icon: Users,
  //     text: (
  //       <>
  //         Not traditional coaching.  
  //         This is an <strong>inductive, investigative, <span className="text-orange-400">performance-anchored</span> engagement</strong> where leaders co-design and implement their own change charters.  
  //         Transformation multiplies organically through daily engagements. Amplified by <strong>Trinity’s AI-guided reflections</strong> and intrinsic motivation.
  //       </>
  //     ),
  //     image: etApproach4,
  //   },
  //   {
  //     title: "What’s the Primary Outcome Focus?",
  //     icon: BarChart2,
  //     text: (
  //       <>
  //         <strong><span className="text-orange-500">Unwavering <span className="text-orange-500 animate-pulse">client focus.</span></span></strong> Understanding, delivering, advising, creating.  
  //         Every link of performance strengthens the organization’s ability to generate <strong>client trust and value</strong>.  
  //         All metrics of success connect directly to how effectively client value is created and sustained.
  //       </>
  //     ),
  //     image: etApproach5,
  //   },
  //   {
  //     title: "Is It for Sales or Delivery?",
  //     icon: Rocket,
  //     text: (
  //       <>
  //         <strong><span className="text-orange-500">Delivery</span> is the differentiator.</strong>  
  //         <span className="text-orange-500 animate-pulse"> Eternity’s belief:</span>  everyone in a tech-led organization  is a potential seller. Capable of influencing, advising, and creating meaningful client impact.  
  //         We build organizations where <strong>delivery excellence equals growth momentum</strong>.
  //       </>
  //     ),
  //     image: etApproach6,
  //   },
  //   {
  //     title: "Why Start with CxOs?",
  //     icon: Sparkles,
  //     text: (
  //       <>
  //         CxOs are the <strong><span className="text-orange-500">prime movers.</span></strong> The most powerful source of energy and inspiration.  
  //         Their active participation defines the <strong><span className="text-orange-500 animate-pulse">magnetic field</span></strong> for the rest of the organization.  
  //         They are catalysts who build a <strong>self-driving, high-performance culture</strong>. Powered and perpetuated through Trinity.
  //       </>
  //     ),
  //     image: etApproach7,
  //   },
  // ];

  // 2️⃣ Animation variants
  
  const fadeVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  // 3️⃣ JSX
  return (
    <section
      id="approach"
      className="relative py-24 bg-black text-white overflow-hidden"
    >
      {/* Animated Pulsating Gradient Background */}
      <div className="absolute inset-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-orange-900/20 to-black" />
        {/* Pulsating orange field */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,102,0,0.25)_0%,transparent_70%)] mix-blend-screen animate-[pulse_10s_ease-in-out_infinite]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.h2
          className="text-center text-3xl md:text-5xl font-bold text-orange-500 mb-6"
          initial="hidden"
          whileInView="visible"
          variants={fadeVariants}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Our Approach to Performance Transformation
        </motion.h2>

        <motion.p
          className="text-center text-gray-300 max-w-3xl mx-auto mb-20 text-lg leading-relaxed"
          initial="hidden"
          whileInView="visible"
          variants={fadeVariants}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
        >
          Expansive. Experiential. Purpose-anchored. Designed to magnetize self-propelling
          change that compounds over time.
        </motion.p>

        {/* Panels */}
        <div className="space-y-32">
          {sections.map((sec, i) => {
            const Icon = sec.icon;
            const isEven = i % 2 === 0;
            return (
              <motion.div
                key={sec.title}
                className={`flex flex-col md:flex-row ${
                  isEven ? "" : "md:flex-row-reverse"
                } items-center gap-10 md:gap-20`}
                initial="hidden"
                whileInView="visible"
                variants={fadeVariants}
                transition={{ duration: 0.8, delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                {/* Text Block */}
                <div className="md:w-1/2 space-y-5">
                  <div className="flex items-center gap-3">
                    <Icon className="h-8 w-8 text-orange-500" />
                    <h3 className="text-2xl md:text-3xl font-semibold text-white">
                      {sec.title}
                    </h3>
                  </div>
                  <p className="text-gray-200 text-base md:text-lg leading-relaxed">
                    {sec.text}
                  </p>
                </div>

                {/* Image Block with Glow */}
                <motion.div
                  className="relative md:w-1/2 rounded-2xl overflow-hidden shadow-2xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  {/* Glow behind image */}
                  {/* <div className="absolute inset-0 bg-gradient-to-tr from-orange-600/20 via-transparent to-transparent blur-2xl" /> */}
                  {/* Ambient glow pulse behind image */}
                  <motion.div
                    className="absolute inset-0 bg-orange-600/10 blur-2xl rounded-2xl"
                    animate={{ opacity: [0.15, 0.35, 0.15] }}
                    transition={{
                      duration: 8,
                      ease: "easeInOut",
                      repeat: Infinity,
                    }}
                  />
                  {/* <img
                    src={sec.image}
                    alt={sec.title}
                    className="w-full h-96 object-cover transition-transform duration-700 ease-out hover:scale-105 border border-orange-900/20"
                  /> */}

                  {/* Floating image */}
                  <motion.img
                    src={sec.image}
                    alt={sec.title}
                    className="relative w-full h-96 object-cover border border-orange-900/20 rounded-2xl"
                    animate={{ y: [0, -10, 0] }}
                    transition={{
                      duration: 10,
                      ease: "easeInOut",
                      repeat: Infinity,
                    }}
                    whileHover={{ scale: 1.05 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-28 text-center">
          <motion.a
            href="/trinity"
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-600 text-white text-lg font-semibold rounded-lg shadow-lg hover:bg-orange-700 transition"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Explore Trinity Engagement
            <Rocket className="h-6 w-6" />
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default EtEternitySolutions;